import type { Scene } from '@babylonjs/core/scene';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import '@babylonjs/core/Particles/particleSystemComponent';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { EffectsConfig } from '../config/effects.config';

/** Soft round puff sprite (radial falloff), drawn once – no texture file needed. */
const SPRITE_SIZE = 64;
/** ParticleSystem.manualEmitCount value that means "emit by emitRate". */
const RATE_EMISSION = -1;

/**
 * Dust kicked up by the player: a steady trail while sliding, a puff when a slide starts and on hard
 * landings. Particles live in world space, so the trail stays behind the runner by itself.
 */
export class DustEffect {
  private readonly system: ParticleSystem;
  private readonly origin = new Vector3();
  private sliding = false;

  constructor(scene: Scene, private readonly root: TransformNode, private readonly config: EffectsConfig['dust']) {
    const c = config;
    const system = (this.system = new ParticleSystem('FX_DUST', c.capacity, scene));
    system.particleTexture = createDustSprite(scene);
    system.emitter = this.origin;
    system.minEmitBox = new Vector3(-c.area.halfWidth, 0, -c.area.halfDepth);
    system.maxEmitBox = new Vector3(c.area.halfWidth, c.area.height, c.area.halfDepth);
    system.direction1 = new Vector3(-0.6, 0.5, -0.6);
    system.direction2 = new Vector3(0.6, 1.2, 0.6);
    system.minEmitPower = c.speed.min;
    system.maxEmitPower = c.speed.max;
    system.gravity = new Vector3(0, c.rise, 0);
    system.minSize = c.size.min;
    system.maxSize = c.size.max;
    system.addSizeGradient(0, 1);
    system.addSizeGradient(1, c.size.growth);
    system.minLifeTime = c.lifetime.min;
    system.maxLifeTime = c.lifetime.max;
    system.minAngularSpeed = -1;
    system.maxAngularSpeed = 1;
    system.color1 = new Color4(...c.color);
    system.color2 = new Color4(...c.color);
    system.colorDead = new Color4(...c.colorFade);
    system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    system.emitRate = 0;
    system.start();
  }

  /** Called every frame with the player's slide state. */
  update(sliding: boolean): void {
    // After a manual burst Babylon leaves manualEmitCount at 0, which disables rate emission – re-enable it.
    if (this.system.manualEmitCount === 0) this.system.manualEmitCount = RATE_EMISSION;
    this.origin.copyFrom(this.root.getAbsolutePosition());
    if (sliding && !this.sliding) this.system.manualEmitCount = this.config.slideBurst;
    this.sliding = sliding;
    this.system.emitRate = sliding ? this.config.slideRate : 0;
  }

  land(impactSpeed: number): void {
    if (impactSpeed < this.config.landMinImpact) return;
    this.origin.copyFrom(this.root.getAbsolutePosition());
    this.system.manualEmitCount = this.config.landBurst;
  }

  reset(): void {
    this.sliding = false;
    this.system.emitRate = 0;
    this.system.reset();
  }
}

/** Soft round puff (radial falloff) for dust particles, shared by the slide dust and the collapse dust. */
export function createDustSprite(scene: Scene): DynamicTexture {
  const texture = new DynamicTexture('FX_DUST_SPRITE', SPRITE_SIZE, scene, false);
  const ctx = texture.getContext();
  const half = SPRITE_SIZE / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, 'rgba(255,255,255,0.9)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.45)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
  texture.hasAlpha = true;
  texture.update();
  return texture;
}
